import { consume } from '@lit/context';
import { LitElement, css, html, nothing, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';

import type { FlagCode } from '../../../../generated/flags';
import { flagSvgPath } from '../../../../services/Flags';
import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy, type LeaderboardEntry } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

/** Distance from the viewport top to the board, in px. Feeds both the host's
 *  CSS top and the row-fit calculation above the minimap. */
const BOARD_TOP = 266;

/**
 * Mini Leaderboard - In-game top-right scoreboard
 *
 * Displays player rankings during gameplay:
 * - Medal badges for top 3 (gold, silver, bronze)
 * - Country flags
 * - Player names with team colors (CTF)
 * - Level badges
 * - Scores with fixed-width digits
 * - Current player highlighting
 * - Ellipsis for truncated lists
 *
 * @element mini-leaderboard
 */
@customElement('mini-leaderboard')
export class MiniLeaderboard extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        position: absolute;
        top: ${unsafeCSS(BOARD_TOP)}px;
        right: 12px;
        width: 258px;
        /* Declared here rather than inherited from the body. The rows are
           clickable, so no text selection either. */
        font-family: var(--font-family);
        font-size: 15px;
        font-weight: bold;
        -webkit-user-select: none;
        user-select: none;
        box-sizing: border-box;
        cursor: default;
        white-space: nowrap;
        pointer-events: none;
        overflow: hidden;
      }

      .ellipsis {
        text-align: center;
        color: var(--color-text-white);
        margin: 4px 0;
        font-size: var(--font-size-lg);
        letter-spacing: 2px;
        text-shadow: var(--shadow-text);
      }

      .entry {
        text-shadow: var(--shadow-text);
        margin-bottom: 2px;
        position: relative;
        line-height: 20px;
      }

      .entry.current {
        padding: 6px 0;
        background-color: var(--color-bg-chip);
        border-radius: 7px;
      }

      .entry.clickable {
        pointer-events: auto;
        cursor: pointer;
      }

      @media (hover: hover) {
        .entry.clickable:hover {
          background-color: var(--color-overlay-subtle);
          border-radius: var(--radius-sm);
        }
      }

      /* Position/rank display */
      .position {
        width: 30px;
        display: inline-block;
        text-align: right;
        color: var(--color-text-bright);
        font-weight: bold;
        margin-right: 4px;
      }

      .position.bigger {
        width: 36px;
        margin-right: 4px;
      }

      .entry.current .position {
        color: var(--color-text-white);
      }

      /* Medal badges */
      .badge {
        display: inline-block;
        width: 20px;
        height: 20px;
        vertical-align: middle;
        mask-image: url('/vanilla/ui/scoreboard/crown.svg');
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-image: url('/vanilla/ui/scoreboard/crown.svg');
        -webkit-mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
      }

      .badge.gold {
        background: var(--gradient-medal-gold);
      }

      .badge.silver {
        background: var(--gradient-medal-silver);
      }

      .badge.bronze {
        background: var(--gradient-medal-bronze);
      }

      /* Flag icon */
      .flag {
        width: 18px;
        height: 18px;
        display: inline-block;
        vertical-align: middle;
        object-fit: contain;
        margin-right: 3px;
      }

      /* Player name */
      .name {
        color: var(--color-text-bright);
        font-weight: bold;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100px;
        display: inline-block;
        vertical-align: middle;
      }

      /* Current-row white applies only to the neutral (FFA/BTR) name.
         :where() keeps this at .name specificity so the CTF team classes
         below outrank it — same outcome as vanilla, where #scoreboard
         .sel .nick loses to .nick.team-N. (A plain .entry.current .name
         silently won instead, turning my own CTF name white.) */
      :where(.entry.current) .name {
        color: var(--color-text-white);
      }

      .name.team-1 {
        color: var(--color-team-blue);
      }

      .name.team-2 {
        color: var(--color-team-red);
      }

      /* StarMash spectator language: the name keeps its identity color
         (team in CTF, white otherwise) with a strikethrough. Vanilla greyed
         name/place/score; the live client dropped the grey entirely. Our
         addition: dim via opacity — hue survives (unlike a grey swap) but
         the fade reads at a glance when the strike alone gets lost in a
         long busy board. */
      .name.spectating {
        text-decoration: line-through;
        opacity: 0.55;
      }

      /* Level badge */
      .level {
        display: inline-block;
        color: var(--color-text-white);
        background-color: rgba(80, 80, 80, 0.75);
        border-radius: var(--radius-md);
        padding: 0 4px;
        margin-left: 4px;
        font-weight: bold;
      }

      .level.bot {
        font-size: var(--font-size-xxs);
        font-weight: lighter;
        color: #eee;
      }

      /* Score display */
      .score {
        position: absolute;
        top: 0;
        right: 8px;
        color: var(--color-text-white);
        font-weight: bold;
        font-variant-numeric: tabular-nums;
      }

      .entry.current .score {
        top: 6px;
      }
    `,
  ];

  /** UI state from context */
  private uiState = bindValtio(this, uiStateProxy);

  /** Game API from context */
  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  /** Gets the SVG path for a flag ISO code */
  private getFlagSrc(flag: FlagCode): string {
    return flagSvgPath(flag);
  }

  /** Opens the player context menu from a row, on left or right click. One
   *  handler for every row, reading the player id from its data attribute, so
   *  Lit keeps a stable listener instead of re-binding fresh closures on every
   *  render (this re-renders on any uiState change). Mirrors game-log. */
  private handleRowClick = (e: MouseEvent): void => {
    e.stopPropagation();
    if (e.type === 'contextmenu') e.preventDefault();
    const row = e.currentTarget;
    if (!(row instanceof HTMLElement)) return;
    const playerId = Number(row.dataset['playerId']);
    if (!Number.isFinite(playerId)) return;
    const rowRect = row.getBoundingClientRect();
    this.api.playClick();
    this.api.showContextMenu(playerId, rowRect.top, rowRect.left + 20);
  };

  /** Renders a single leaderboard entry */
  private renderEntry(entry: LeaderboardEntry) {
    const isTopThree = entry.rank <= 3;
    const medalClass = isTopThree ? ['', 'gold', 'silver', 'bronze'][entry.rank] : '';
    const isSpectating = entry.isSpectating && !entry.isBot;
    const canClick = !entry.isMe && !entry.isBot;

    return html`
      <div
        class=${classMap({
          entry: true,
          current: entry.isMe,
          clickable: canClick,
        })}
        data-player-id=${entry.id}
        @click=${canClick ? this.handleRowClick : nothing}
        @contextmenu=${canClick ? this.handleRowClick : nothing}
      >
        <span
          class=${classMap({
            position: true,
            bigger: String(entry.rank).length >= 3,
          })}
        >
          ${isTopThree ? html`<span class="badge ${medalClass}"></span>` : `${entry.rank}.`}
        </span>
        <img class="flag" src="${this.getFlagSrc(entry.flag)}" alt="" draggable="false" />
        <span
          class=${classMap({
            name: true,
            'team-1': entry.team === 1,
            'team-2': entry.team === 2,
            spectating: isSpectating,
          })}
        >
          ${entry.name}
        </span>
        ${
          entry.level ? html`<span class=${classMap({ level: true, bot: entry.isBot })}>${entry.level}</span>` : nothing
        }
        <span class="score">${entry.score}</span>
      </div>
    `;
  }

  /** Calculate how many entries can fit before overlapping minimap */
  private getMaxVisibleEntries(): number {
    const margin = 16; // breathing room between leaderboard and minimap
    const entryHeight = 24; // ~22px normal + some buffer
    const minimapTop = this.uiState.minimapRect.top;

    // Available height = minimap top - leaderboard top - margin
    const availableHeight = minimapTop - BOARD_TOP - margin;

    if (availableHeight <= 0) return 3; // minimum: gold, ellipsis, me
    return Math.max(3, Math.floor(availableHeight / entryHeight));
  }

  /** Render ellipsis row */
  private renderEllipsis() {
    return html`<div class="ellipsis">&middot; &middot; &middot;</div>`;
  }

  override render() {
    // Mobile: not rendered — on landscape phones the host box (top: 266px,
    // right edge) physically overlaps the fire/special touch zones. May return
    // in a future mobile HUD rework as an expandable hub surface.
    if (this.uiState.isMobile) return nothing;
    if (this.uiState.leaderboardHidden) return nothing;
    const leaderboard = this.uiState.leaderboard;
    if (!leaderboard) return nothing;

    // Filter out bots if ignoreBots is enabled. Ranks are NOT renumbered —
    // hiding bots hides their rows, it doesn't rewrite the standings, so the
    // visible ranks may jump (1., 10., 21.). Rank jumps are therefore NOT a
    // truncation signal here; truncation is decided purely by available
    // space and the producer's showEllipsisBefore flag (its off-board "me"
    // substitution, where me is always the final entry).
    const allEntries = this.uiState.gameSettings.ignoreBots
      ? leaderboard.entries.filter((e) => !e.isBot)
      : leaderboard.entries;

    const maxVisible = this.getMaxVisibleEntries();
    const meEntry = allEntries.find((e) => e.isMe);
    const meIndex = meEntry ? allEntries.indexOf(meEntry) : -1;

    // Everything fits: show every row. Producer-side substitution still gets
    // its dots before the trailing me row; a producer-side cut below the
    // board (showEllipsisAfter) gets trailing dots.
    if (allEntries.length <= maxVisible) {
      const dotsBeforeMe = leaderboard.showEllipsisBefore && meEntry && allEntries[allEntries.length - 1] === meEntry;
      const headEntries = dotsBeforeMe ? allEntries.slice(0, -1) : allEntries;
      return html`
        ${repeat(
          headEntries,
          (e) => e.id,
          (e) => this.renderEntry(e),
        )}
        ${dotsBeforeMe && meEntry ? html`${this.renderEllipsis()} ${this.renderEntry(meEntry)}` : nothing}
        ${leaderboard.showEllipsisAfter ? this.renderEllipsis() : nothing}
      `;
    }

    // Space-limited. If me is safely inside the top slice (or absent), show
    // the top rows and mark the cut tail with dots.
    if (meIndex === -1 || meIndex <= maxVisible - 2) {
      const topEntries = allEntries.slice(0, maxVisible - 1);
      return html`
        ${repeat(
          topEntries,
          (e) => e.id,
          (e) => this.renderEntry(e),
        )}
        ${this.renderEllipsis()}
      `;
    }

    // Me beyond the visible top: top (maxVisible - 2) + dots + me.
    const topEntries = allEntries.slice(0, maxVisible - 2);
    return html`
      ${repeat(
        topEntries,
        (e) => e.id,
        (e) => this.renderEntry(e),
      )}
      ${this.renderEllipsis()} ${meEntry ? this.renderEntry(meEntry) : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mini-leaderboard': MiniLeaderboard;
  }
}
